import { useState, useRef, useCallback } from "react";
import { apiClient } from "@/api";

export interface SearchUser {
    id: number;
    name: string;
    email: string;
    mobile: string | null;
    phone: string | null;
    status: string;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const useGlobalSearch = () => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<SearchUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const search = useCallback(async (value: string) => {
        if (value.trim().length < MIN_QUERY_LENGTH) {
            setResults([]);
            setHasSearched(false);
            return;
        }

        // Cancel any in-flight request
        abortRef.current?.abort();
        abortRef.current = new AbortController();

        setIsLoading(true);
        try {
            const { data } = await apiClient.get<SearchUser[]>("/global-search", {
                params: { search: value.trim() },
                signal: abortRef.current.signal,
            });
            setResults(data);
            setHasSearched(true);
        } catch (err: unknown) {
            // Ignore abort errors (user typed again)
            if (err instanceof Error && err.name !== "CanceledError" && err.name !== "AbortError") {
                setResults([]);
                setHasSearched(true);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (value.trim().length < MIN_QUERY_LENGTH) {
            setResults([]);
            setHasSearched(false);
            return;
        }

        debounceRef.current = setTimeout(() => search(value), DEBOUNCE_MS);
    }, [search]);

    const reset = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();
        setQuery("");
        setResults([]);
        setHasSearched(false);
        setIsLoading(false);
    }, []);

    return {
        query,
        results,
        isLoading,
        hasSearched,
        handleQueryChange,
        reset,
    };
};

export default useGlobalSearch;
